import { CheckCircle2, Loader2, ChevronDown, Database, Globe, FileText, ListTodo, Users } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import type { ChatContextData } from '@/types/leadChat';
import { useState } from 'react';
import { cn } from '@/lib/utils';

interface ContextIndicatorProps {
  isLoading: boolean;
  contextData: ChatContextData | null;
  error?: string | null;
}

export const ContextIndicator = ({ isLoading, contextData, error }: ContextIndicatorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (error) {
    return (
      <div className="px-4 py-3 bg-gradient-to-r from-destructive/10 to-destructive/5 border-b border-destructive/20">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <div className="h-2 w-2 rounded-full bg-destructive animate-pulse" />
          <span className="font-medium">Failed to load context: {error}</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="px-4 py-3 bg-gradient-to-r from-muted to-muted/50 border-b">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="font-medium">Loading conversation context...</span>
          </div>
          <Progress value={45} className="h-1.5" />
        </div>
      </div>
    );
  }

  if (!contextData) return null;

  const contextItems = [
    { 
      label: 'Lead details', 
      loaded: true, 
      icon: Database,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    { 
      label: 'Web search data', 
      loaded: contextData.hasWebSearchData, 
      icon: Globe,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
    { 
      label: `Notes (${contextData.notesCount})`, 
      loaded: contextData.hasNotes, 
      icon: FileText,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10'
    },
    { 
      label: `Tasks (${contextData.tasksCount})`, 
      loaded: contextData.hasTasks, 
      icon: ListTodo,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
    { 
      label: `Contacts (${contextData.contactsCount})`, 
      loaded: contextData.hasContacts, 
      icon: Users,
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10'
    },
  ];

  const loadedCount = contextItems.filter(item => item.loaded).length;
  const progressPercentage = (loadedCount / contextItems.length) * 100;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full px-4 py-3 bg-gradient-to-r from-muted via-muted to-muted/50 border-b hover:from-muted hover:to-muted transition-all group">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-sm font-semibold text-foreground">
                Context Ready
              </span>
            </div>
            <Badge variant="secondary" className="text-xs font-semibold px-2 py-0 h-5">
              {loadedCount}/{contextItems.length} Sources
            </Badge>
          </div>
          <ChevronDown className={cn(
            "w-4 h-4 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )} />
        </div>
        <Progress value={progressPercentage} className="h-1 mt-2" />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 py-3 bg-gradient-to-b from-muted/50 to-transparent border-b">
        <div className="grid grid-cols-1 gap-2">
          {contextItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div 
                key={index} 
                className={cn(
                  "flex items-center gap-3 p-2 rounded-lg transition-all",
                  item.loaded ? item.bgColor : "bg-muted/30"
                )}
              >
                <div className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-lg",
                  item.loaded ? item.bgColor : "bg-muted"
                )}>
                  <Icon className={cn(
                    "w-4 h-4",
                    item.loaded ? item.color : "text-muted-foreground/50"
                  )} />
                </div>
                <div className="flex-1 flex items-center justify-between">
                  <span className={cn(
                    "text-sm font-medium",
                    item.loaded ? "text-foreground" : "text-muted-foreground"
                  )}>
                    {item.label}
                  </span>
                  {item.loaded ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
